import React, { useState } from 'react';
import { exportCustomerData, eraseCustomer, errorMessage } from '../services/api';
import { useLanguage } from '../i18n/LanguageContext';

/**
 * Privacy (GDPR) di un cliente, nella sua scheda: scarica tutti i suoi dati (richiesta di accesso) oppure li anonimizza o li elimina
 * (richiesta di cancellazione). Per le azioni irreversibili si deve riscrivere l'indirizzo email del cliente.
 */
function CustomerPrivacyActions({ customer, onDone }) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('anonymize'); // anonymize | identity | delete
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const download = async () => {
    setError('');
    setMessage('');
    try {
      const blob = await exportCustomerData(customer.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dati-cliente-${customer.id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMessage(t('privacy.exported'));
    } catch (err) {
      setError(errorMessage(err, t('privacy.exportError')));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await eraseCustomer(customer.id, {
        mode: mode === 'delete' ? 'delete' : 'anonymize',
        redact_content: mode !== 'identity',
        confirm_email: confirm.trim(),
      });
      setOpen(false);
      setMessage(t(res.mode === 'delete' ? 'privacy.deleted' : 'privacy.anonymized', { count: res.tickets }));
      if (onDone) onDone();
    } catch (err) {
      setError(errorMessage(err, t('privacy.eraseError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pv-box" onClick={(e) => e.stopPropagation()}>
      <strong>{t('privacy.title')}</strong>
      <div className="pv-actions">
        <button type="button" className="btn btn-secondary btn-sm" onClick={download}>{t('privacy.export')}</button>
        {!open && <button type="button" className="btn btn-secondary btn-sm" onClick={() => setOpen(true)}>{t('privacy.erase')}</button>}
      </div>
      {message && <div className="success-message">{message}</div>}
      {error && <div className="error-message">{error}</div>}
      {open && (
        <form onSubmit={submit} className="pv-form">
          <label className="rp-check"><input type="radio" name={`pv-${customer.id}`} checked={mode === 'anonymize'} onChange={() => setMode('anonymize')} /> {t('privacy.modeAnonymize')}</label>
          <label className="rp-check"><input type="radio" name={`pv-${customer.id}`} checked={mode === 'identity'} onChange={() => setMode('identity')} /> {t('privacy.modeIdentity')}</label>
          <label className="rp-check"><input type="radio" name={`pv-${customer.id}`} checked={mode === 'delete'} onChange={() => setMode('delete')} /> {t('privacy.modeDelete')}</label>
          <p className="tp-hint">{t('privacy.irreversible')}</p>
          <div className="form-group" style={{ maxWidth: 380 }}>
            <label>{t('privacy.confirmLabel', { email: customer.email })}</label>
            <input value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={customer.email} autoComplete="off" required />
          </div>
          <div className="pv-actions">
            <button type="submit" className="btn btn-sm" disabled={busy || confirm.trim().toLowerCase() !== String(customer.email).toLowerCase()}>
              {mode === 'delete' ? t('privacy.doDelete') : t('privacy.doAnonymize')}
            </button>
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setOpen(false); setConfirm(''); setError(''); }}>{t('common.cancel')}</button>
          </div>
        </form>
      )}
    </div>
  );
}

export default CustomerPrivacyActions;
